// src/hooks/useAgentChat.js
// SSE fetch consumer for the AI Chat Assistant.
// Handles: sending messages, reading SSE stream, dispatching events to context,
// abort/cleanup on unmount.

import { useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { useAgentChatContext } from '../context/AgentChatContext';
import { useToast } from '../components/Toast';
import { logger } from '../utils/logger';

// Cloud Function URL — from firebase deploy output.
// Format: https://{region}-{projectId}.cloudfunctions.net/{functionName}
const AGENT_CHAT_URL =
  process.env.REACT_APP_AGENT_CHAT_URL ||
  'https://us-central1-mora-development.cloudfunctions.net/agentChat';

export default function useAgentChat() {
  const {
    activeSessionId,
    activeConversationId,
    pageContext,
    setIsStreaming,
    setCurrentTool,
    setStreamingMessage,
    setActiveConversationId,
    setActiveSessionId,
    setError,
  } = useAgentChatContext();
  const { showToast } = useToast();
  const abortRef = useRef(null);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim()) return;

      // Get Firebase ID token
      const user = auth.currentUser;
      if (!user) {
        showToast('Please sign in to use the AI assistant', 'error');
        return;
      }

      const idToken = await user.getIdToken();

      // Abort any existing stream
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setIsStreaming(true);
      setStreamingMessage('');
      setCurrentTool(null);
      setError(null);

      // Client-side cancel must NOT fire before the server's own stream
      // ceiling (MAX_STREAM_TIME_MS = 270000 in functions/agentSession.js).
      // Otherwise multi-tool chats that legitimately run long show a
      // phantom "timed out" toast while the server is still producing.
      // The Cloud Function deployment kills at 300s, so 270s is the real
      // ceiling on both sides.
      const timeoutId = setTimeout(() => {
        if (abortRef.current) {
          abortRef.current.abort();
          setActiveSessionId(null);
          setError('Request timed out — please try again');
          showToast('Request timed out — please try again', 'warning');
          setIsStreaming(false);
        }
      }, 270000);

      try {
        const res = await fetch(AGENT_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            message: text.trim(),
            sessionId: activeSessionId || null,
            conversationId: activeConversationId || null,
            context: pageContext,
          }),
          signal: controller.signal,
        });

        // Handle non-SSE error responses
        if (!res.ok) {
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errData = await res.json();
            if (res.status === 429) {
              showToast(`Too many messages. Try again in ${errData.retryAfter || 60}s`, 'warning');
            } else if (res.status === 401) {
              showToast('Session expired — please refresh the page', 'error');
            } else {
              showToast(errData.error || 'Chat failed', 'error');
            }
            setIsStreaming(false);
            return;
          }
          throw new Error(`Chat request failed (${res.status})`);
        }

        // Read SSE stream via ReadableStream
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
          const lines = buffer.split('\n\n');
          buffer = lines.pop(); // Keep incomplete chunk

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'session_created':
                  setActiveSessionId(data.sessionId);
                  if (data.conversationId) setActiveConversationId(data.conversationId);
                  break;

                case 'agent.message':
                  // Accumulate text from content blocks
                  for (const block of data.content || []) {
                    if (block.type === 'text') {
                      accumulated += block.text;
                      setStreamingMessage(accumulated);
                    }
                  }
                  break;

                case 'tool.started':
                  setCurrentTool(data.tool);
                  break;

                case 'tool.completed':
                  setCurrentTool(null);
                  break;

                case 'done':
                  setIsStreaming(false);
                  setStreamingMessage('');
                  break;

                case 'error':
                  setActiveSessionId(null);
                  setError(data.message);
                  showToast(`AI error: ${data.message}`, 'error');
                  setIsStreaming(false);
                  break;

                default:
                  break;
              }
            } catch {
              // Skip malformed SSE data
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // User cancelled — not an error
        logger.error('agentChat stream error:', err);
        setActiveSessionId(null);
        setError('Connection lost — try again');
        showToast('Connection lost — try again', 'error');
      } finally {
        clearTimeout(timeoutId);
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [
      activeSessionId,
      activeConversationId,
      pageContext,
      setIsStreaming,
      setCurrentTool,
      setStreamingMessage,
      setActiveConversationId,
      setActiveSessionId,
      setError,
      showToast,
    ]
  );

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, [setIsStreaming]);

  return { sendMessage, cancelStream };
}
